import { API } from 'aws-amplify';
import { ViewAsScopeService } from './view-as-scope.service';

type ApiMethod = (apiName: string, path: string, init?: unknown) => Promise<unknown>;

function mergeScopeIntoInit(
	init: unknown,
	viewAsScopeService: ViewAsScopeService
): unknown {
	if (!viewAsScopeService.isEnabled()) {
		return init;
	}

	const scope = viewAsScopeService.getScope();
	if (!scope || !scope.enabled) {
		return init;
	}

	const scopeParams: Record<string, string> = {};
	if (scope.distiId != null && scope.distiId !== '') {
		scopeParams.disti_id = String(scope.distiId);
	}
	if (scope.clientId != null && scope.clientId !== '') {
		scopeParams.client_id = String(scope.clientId);
	}
	if (scope.campusId != null && scope.campusId !== '') {
		scopeParams.campus_id = String(scope.campusId);
	}

	if (Object.keys(scopeParams).length === 0) {
		return init;
	}

	const initObj = init && typeof init === 'object' ? (init as Record<string, unknown>) : {};
	const merged = { ...initObj };

	// Merge into queryStringParameters (do not overwrite existing)
	const existingQs =
		merged.queryStringParameters && typeof merged.queryStringParameters === 'object'
			? (merged.queryStringParameters as Record<string, string>)
			: {};
	const mergedQs = { ...existingQs };
	for (const [key, value] of Object.entries(scopeParams)) {
		if (mergedQs[key] === undefined || mergedQs[key] === null || mergedQs[key] === '') {
			mergedQs[key] = value;
		}
	}
	merged.queryStringParameters = mergedQs;

	// Merge into body when it's a plain object (POST/PUT)
	if (merged.body && typeof merged.body === 'object' && !Array.isArray(merged.body)) {
		const body = merged.body as Record<string, unknown>;
		const mergedBody = { ...body };
		for (const [key, value] of Object.entries(scopeParams)) {
			if (
				mergedBody[key] === undefined ||
				mergedBody[key] === null ||
				mergedBody[key] === ''
			) {
				mergedBody[key] = value;
			}
		}
		merged.body = mergedBody;
	}

	return merged;
}

function wrapApiMethod(
	original: ApiMethod,
	viewAsScopeService: ViewAsScopeService
): ApiMethod {
	return async (apiName: string, path: string, init?: unknown) => {
		const mergedInit = mergeScopeIntoInit(init, viewAsScopeService);
		return original.call(API, apiName, path, mergedInit);
	};
}

/**
 * Patches the Amplify API to inject View As scope params (client_id, campus_id, disti_id)
 * into all requests when View As is enabled.
 * Must be called after Amplify.configure() in app initialization.
 */
export function patchAmplifyApiForViewAsScope(
	viewAsScopeService: ViewAsScopeService
): void {
	const originalGet = API.get.bind(API);
	const originalPost = API.post.bind(API);
	const originalPut = API.put.bind(API);
	const originalDel = API.del.bind(API);

	(API as { get: ApiMethod }).get = wrapApiMethod(originalGet as ApiMethod, viewAsScopeService);
	(API as { post: ApiMethod }).post = wrapApiMethod(originalPost as ApiMethod, viewAsScopeService);
	(API as { put: ApiMethod }).put = wrapApiMethod(originalPut as ApiMethod, viewAsScopeService);
	(API as { del: ApiMethod }).del = wrapApiMethod(originalDel as ApiMethod, viewAsScopeService);
}
